import React, {
    useContext
} from 'react';

import PropTypes from 'prop-types';

import {
    useTranslation
} from '../../../../i18n.js';

import {
    useGetFocusedInstanceIdValue,
    useComputedCurationConfigValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import LandscapeContext from '../../../contexts/landscapeContext.js';

import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';
import MetricTableCell from '../../../components/table/MetricTableCell.jsx';
import Panel from '../../../components/Panel.jsx';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';
import useGetPostTags from '../hooks/useGetPostTags.js';
import RIQLoading from '../../../components/RIQLoading.jsx';

function NoDataDuringPeriod() {
    return (
        <div className='tlw-flex tlw-h-[180px] tlw-items-center tlw-justify-center tlw-text-lg tlw-font-light'>No data in this time period</div>
    );
}

function GetPostTags(metricResults) {
    let postTagsById;
    const {data: postTags, isLoading} = useGetPostTags();
    if (!isLoading) {
        postTagsById = _.keyBy(postTags, 'id');
        postTagsById.__UNTAGGED__ = {
            tag_name: 'Untagged',
            postTagHtml: 'Untagged'
        };

        _.forEach(metricResults, results => {
            _.map(results.data, function (value) {
                value[0] = RivalIQ.postTags.utils.makePostTagName(postTagsById || {}, value[0]);
            });
        });
    }
    return isLoading;
}

function DimensionAnalysisTable({dimension, metricResults, metricRequests, focusedInstanceId}) {

    const {
        t
    } = useTranslation('apps/insights/components/DimensionAnalysis');

    let isLoading = false;
    if (dimension === 'postTagId') {
        isLoading = GetPostTags(metricResults);
    }
    if (isLoading) {
        return <RIQLoading />;
    }

    const metricColumns = ['videoViews', 'engagementTotal', 'engagementRateByView'];
    if (focusedInstanceId) {
        metricColumns.push('videoViewsPerPost', 'engagementTotalPerPost', 'likes', 'comments', 'shares');
    }

    const collatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults);

    return (<>
        <div className='tlw-overflow-x-auto max-sm_tlw-w-full max-sm_tlw-overflow-y-hidden'>
            <table className={`tlw-w-full`}>
                <thead>
                    <tr className='tlw-bg-gray-150'>
                        <MetricTableHeader
                            key={`${dimension}`}
                            className={`tlw-w-1/3 tlw-min-w-[150px] max-sm_tlw-whitespace-nowrap`}
                            title={t(`${dimension}.label`)}

                        />
                        <MetricTableHeader
                            key={'videos'}
                            title={t(`metricColumn.videos`)}
                            description={t(`metricDescription.videos`)}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[60px] max-sm_tlw-whitespace-nowrap'
                        />
                        {_.map(metricColumns, (measure) => {
                            return (
                                <MetricTableHeader
                                    key={measure}
                                    title={t(`metricColumn.${measure}`)}
                                    description={t(`metricDescription.${measure}`)}
                                    alignment={MetricTableHeader.Alignment.RIGHT}
                                    className='tlw-min-w-[60px] max-sm_tlw-whitespace-nowrap'
                                />
                            );
                        })}
                    </tr>
                </thead>

                <tbody>
                    {_.map(collatedResults, (data) => {
                        return (
                            <tr
                                key={data.dimensionValue[0]}
                                className='tlw-h-[47px] tlw-cursor-pointer tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50'>
                                <MetricTableCell
                                    className={`tlw-w-full tlw-truncate tlw-text-left`}
                                    abbreviatedValue={dimension === 'hashtag' ? `#${data.dimensionValue[0]}` : data.dimensionValue[0]}
                                    metricName={dimension}
                                    alignment={MetricTableCell.Alignment.LEFT}
                                />
                                <MetricTableCell
                                    className={`tlw-w-full tlw-truncate tlw-text-right`}
                                    abbreviatedValue={data.dimensionValue[1]}
                                    metricName={`${dimension}-videos`}
                                    alignment={MetricTableCell.Alignment.RIGHT}
                                />
                                {_.map(metricColumns, (measure) => {
                                    return (
                                        <MetricTableCell
                                            className={`tlw-w-full tlw-truncate tlw-text-right`}
                                            abbreviatedValue={_.get(data, measure)[1]}
                                            metricName={`${dimension}-${measure}`}
                                            alignment={MetricTableCell.Alignment.RIGHT}
                                        />
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </>);
}

export default function DimensionAnalysis({instanceId, dimension, appSpecificExportConfig}) {

    const {
        t
    } = useTranslation('apps/insights/components/DimensionAnalysis');

    const title = t(`${dimension}.title`);

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig.timePeriodStr
        }
    });

    const landscape = useContext(LandscapeContext);

    const timezone = useCurationConfigValue(instanceId, 'timezone');
    const companyId = useCurationConfigValue(instanceId, 'companyId');
    const dateRanges = useComputedCurationConfigValue(instanceId, 'dateRanges');
    const searchTerm = useCurationConfigValue(instanceId, 'searchTerm');

    const focusedInstanceId = useGetFocusedInstanceIdValue();
    const rowLimit = (focusedInstanceId === instanceId ? -1 : 8);

    const filters = {
        company: {
            onlyCompanyIds: [companyId],
            landscapeId: landscape.id
        },
        timePeriod: {
            startDate: dateRanges.main.start,
            endDate: dateRanges.main.end,
            timezone: timezone
        },
        ordering: {
            sort: [{
                sortOrder: 'desc',
                measure: 'distinctCount(post_id)'
            }],
            limit: rowLimit
        }
    };

    const metricRequests = defaultMetricRequests(dimension, filters, focusedInstanceId);

    if (searchTerm) {
        filters.publicPost = {
            searchTerm: searchTerm
        };
    }

    const csvMetricFilters = JSON.parse(JSON.stringify(filters));
    csvMetricFilters.ordering.limit = -1;
    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat)),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        appSpecificExportConfig: appSpecificExportConfig,
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            headerNameOverrides: [
                'videos',
                'video_views',
                'engagement_total',
                'engagement_rate_by_view',
                'views_per_video',
                'engagement_total_per_video',
                'likes',
                'comments',
                'shares'
            ],
            metricGroups: [
                _.values(defaultMetricRequests(dimension, csvMetricFilters, true))
            ],
            includeDimensionColumn: true
        }
    });

    return (
        <Panel
            instanceId={instanceId}
            title={title}
            exportSubtitle={appSpecificExportConfig.timePeriodStr}
            description={t(`${dimension}.description`)}
            showViewMore={true}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}
            >
                {
                    (metricResults) => {
                        let main = <NoDataDuringPeriod />;
                        const data = metricResults.dimensionValue.data;
                        if (!_.isEmpty(data)) {
                            main = (
                                <DimensionAnalysisTable
                                    dimension={dimension}
                                    metricResults={metricResults}
                                    metricRequests={metricRequests}
                                    focusedInstanceId={focusedInstanceId}
                                />
                            );
                        }
                        return (
                            main
                        );
                    }
                }
            </MetricLoadingView>
        </Panel>

    );
}

function defaultMetricRequests(dimension, filters, focusedInstanceId) {
    const metricRquests = {
        dimensionValue: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            measure: 'distinctCount(post_id)',
            filters: filters
        },
        videoViews: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'sum'
            }],
            measure: 'video_views',
            filters: filters
        },
        engagementTotal: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'sum'
            }],
            measure: 'engagement_total',
            filters: filters
        },
        engagementRateByView: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'average'
            }],
            measure: 'engagement_rate_by_view',
            filters: filters
        }
    };

    if (focusedInstanceId) {
        metricRquests.videoViewsPerPost = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'average'
            }],
            measure: 'video_views',
            filters: filters
        };
        metricRquests.engagementTotalPerPost = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'average'
            }],
            measure: 'engagement_total',
            filters: filters
        };
        metricRquests.likes = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'sum'
            }],
            measure: 'likes',
            filters: filters
        };
        metricRquests.comments = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'sum'
            }],
            measure: 'comments',
            filters: filters
        };
        metricRquests.shares = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: [dimension],
            dimensionReductions: [{
                'post_id': 'sum'
            }],
            measure: 'shares',
            filters: filters
        };
    }
    return metricRquests;
}

DimensionAnalysis.propTypes = {
    instanceId: PropTypes.string.isRequired,
    className: PropTypes.string,
    dimension: PropTypes.string.isRequired,
    dimensionTitle: PropTypes.string,
    pdfExportConfig: PropTypes.object,
    appSpecificExportConfig: PropTypes.object
};
